/**
 * Model benchmark data for intelligent routing
 * Data based on artificialanalysis.ai benchmarks (Jan 2026)
 * Update periodically as new models are released
 */

export interface ModelBenchmark {
  provider: string;
  model: string;
  qualityScore: number;  // 0-100, higher is better
  pricePerMToken: number; // USD per million tokens (blended 3:1 input/output)
  speedTps: number;       // tokens per second
  contextWindow: number;  // max tokens
  capabilities: string[]; // 'code', 'reasoning', 'general', 'fast'
}

export const MODEL_BENCHMARKS: ModelBenchmark[] = [
  // Anthropic
  { provider: "anthropic", model: "claude-opus-4-5-20251101", qualityScore: 95, pricePerMToken: 15.0, speedTps: 50, contextWindow: 200000, capabilities: ["code", "reasoning", "general"] },
  { provider: "anthropic", model: "claude-sonnet-4-20251114", qualityScore: 88, pricePerMToken: 3.0, speedTps: 80, contextWindow: 200000, capabilities: ["code", "reasoning", "general", "fast"] },

  // Z.AI (GLM)
  { provider: "zai", model: "glm-4.7", qualityScore: 82, pricePerMToken: 0.5, speedTps: 100, contextWindow: 128000, capabilities: ["code", "general", "fast"] },
  { provider: "zai", model: "glm-4.5-air", qualityScore: 75, pricePerMToken: 0.1, speedTps: 150, contextWindow: 128000, capabilities: ["general", "fast"] },

  // OpenAI
  { provider: "openai", model: "gpt-4o", qualityScore: 90, pricePerMToken: 5.0, speedTps: 70, contextWindow: 128000, capabilities: ["code", "reasoning", "general"] },
  { provider: "openai", model: "gpt-4o-mini", qualityScore: 78, pricePerMToken: 0.3, speedTps: 120, contextWindow: 128000, capabilities: ["general", "fast"] },
  { provider: "openai", model: "o1", qualityScore: 94, pricePerMToken: 15.0, speedTps: 30, contextWindow: 128000, capabilities: ["reasoning"] },

  // Google Gemini
  { provider: "gemini", model: "gemini-2.0-flash-exp", qualityScore: 85, pricePerMToken: 0.075, speedTps: 200, contextWindow: 1000000, capabilities: ["code", "general", "fast"] },
  { provider: "gemini", model: "gemini-1.5-pro", qualityScore: 87, pricePerMToken: 1.25, speedTps: 60, contextWindow: 2000000, capabilities: ["code", "reasoning", "general"] },

  // DeepSeek
  { provider: "deepseek", model: "deepseek-reasoner", qualityScore: 91, pricePerMToken: 2.19, speedTps: 40, contextWindow: 64000, capabilities: ["reasoning", "code"] },
  { provider: "deepseek", model: "deepseek-chat", qualityScore: 83, pricePerMToken: 0.27, speedTps: 90, contextWindow: 64000, capabilities: ["code", "general", "fast"] },

  // Grok
  { provider: "grok", model: "grok-2", qualityScore: 84, pricePerMToken: 2.0, speedTps: 80, contextWindow: 131072, capabilities: ["code", "general"] },

  // Mistral
  { provider: "mistral", model: "mistral-large-latest", qualityScore: 86, pricePerMToken: 2.0, speedTps: 70, contextWindow: 128000, capabilities: ["code", "reasoning", "general"] },
  { provider: "mistral", model: "codestral-latest", qualityScore: 84, pricePerMToken: 0.3, speedTps: 100, contextWindow: 32000, capabilities: ["code", "fast"] },

  // Ollama (local - no cost but quality varies)
  { provider: "ollama", model: "llama3.2", qualityScore: 70, pricePerMToken: 0, speedTps: 50, contextWindow: 128000, capabilities: ["general"] },
  { provider: "ollama", model: "qwen3:32b", qualityScore: 78, pricePerMToken: 0, speedTps: 30, contextWindow: 32000, capabilities: ["code", "general"] },
];

/**
 * Role requirements for intelligent matching
 */
export interface RoleRequirements {
  role: string;
  requiredCapabilities: string[];
  priorityTier: "critical" | "standard" | "bulk";
  minQualityScore: number;
}

export const ROLE_REQUIREMENTS: RoleRequirements[] = [
  { role: "planner", requiredCapabilities: ["reasoning"], priorityTier: "critical", minQualityScore: 85 },
  { role: "security", requiredCapabilities: ["reasoning", "code"], priorityTier: "critical", minQualityScore: 88 },
  { role: "debugger", requiredCapabilities: ["code", "reasoning"], priorityTier: "critical", minQualityScore: 82 },
  { role: "reviewer", requiredCapabilities: ["code"], priorityTier: "standard", minQualityScore: 80 },
  { role: "adversarial", requiredCapabilities: ["reasoning"], priorityTier: "standard", minQualityScore: 85 },
  { role: "researcher", requiredCapabilities: ["general"], priorityTier: "standard", minQualityScore: 75 },
  { role: "coder", requiredCapabilities: ["code"], priorityTier: "bulk", minQualityScore: 70 },
  { role: "tester", requiredCapabilities: ["code"], priorityTier: "bulk", minQualityScore: 70 },
  { role: "refactorer", requiredCapabilities: ["code"], priorityTier: "bulk", minQualityScore: 70 },
  { role: "documentor", requiredCapabilities: ["general"], priorityTier: "bulk", minQualityScore: 60 },
];

export type OptimizationPreference = "quality" | "balanced" | "cheap";

/**
 * Find the best model for a role based on optimization preference
 */
export function findBestModelForRole(
  role: string,
  availableProviders: string[],
  preference: OptimizationPreference
): { provider: string; model: string } | null {
  const requirements = ROLE_REQUIREMENTS.find(r => r.role === role);
  if (!requirements) return null;

  // Filter models by available providers and required capabilities
  const eligibleModels = MODEL_BENCHMARKS.filter(m => {
    if (!availableProviders.includes(m.provider)) return false;
    return requirements.requiredCapabilities.every(cap => m.capabilities.includes(cap));
  });

  if (eligibleModels.length === 0) return null;

  // Adjust quality threshold based on preference and priority tier
  let minQuality = requirements.minQualityScore;
  if (preference === "cheap") {
    minQuality = Math.max(minQuality - 15, 50);
  } else if (preference === "quality") {
    minQuality = Math.min(minQuality + 5, 95);
  }

  // For "cheap" preference, prioritize cost; for "quality", prioritize score
  // For "balanced", use tier-based approach
  let sortedModels: ModelBenchmark[];

  if (preference === "quality") {
    // Sort by quality descending
    sortedModels = [...eligibleModels].sort((a, b) => b.qualityScore - a.qualityScore);
  } else if (preference === "cheap") {
    // Sort by price ascending, but filter by minimum quality
    sortedModels = eligibleModels
      .filter(m => m.qualityScore >= minQuality)
      .sort((a, b) => a.pricePerMToken - b.pricePerMToken);
  } else {
    // Balanced: critical roles get quality, bulk roles get cheap
    if (requirements.priorityTier === "critical") {
      sortedModels = [...eligibleModels].sort((a, b) => b.qualityScore - a.qualityScore);
    } else if (requirements.priorityTier === "bulk") {
      sortedModels = eligibleModels
        .filter(m => m.qualityScore >= minQuality)
        .sort((a, b) => a.pricePerMToken - b.pricePerMToken);
    } else {
      // Standard tier: balance quality and cost (quality-to-price ratio)
      sortedModels = eligibleModels
        .filter(m => m.qualityScore >= minQuality)
        .sort((a, b) => {
          const ratioA = a.qualityScore / (a.pricePerMToken + 0.01);
          const ratioB = b.qualityScore / (b.pricePerMToken + 0.01);
          return ratioB - ratioA;
        });
    }
  }

  const best = sortedModels[0];
  return best ? { provider: best.provider, model: best.model } : null;
}

/**
 * Generate intelligent role assignments for all roles
 */
export function generateIntelligentAssignments(
  availableProviders: string[],
  preference: OptimizationPreference
): Map<string, { provider: string; model: string }> {
  const assignments = new Map<string, { provider: string; model: string }>();

  for (const req of ROLE_REQUIREMENTS) {
    const best = findBestModelForRole(req.role, availableProviders, preference);
    if (best) {
      assignments.set(req.role, best);
    }
  }

  return assignments;
}

/**
 * Get a human-readable description of the optimization preference
 */
export function getPreferenceDescription(preference: OptimizationPreference): string {
  switch (preference) {
    case "quality":
      return "Using top-tier models for all tasks";
    case "balanced":
      return "Quality for critical tasks, cost-effective for bulk work";
    case "cheap":
      return "Minimizing costs while maintaining acceptable quality";
    default:
      return "";
  }
}

/**
 * Get model benchmark info for display
 */
export function getModelBenchmark(provider: string, model: string): ModelBenchmark | undefined {
  return MODEL_BENCHMARKS.find(m => m.provider === provider && m.model === model);
}
